import React, { useCallback, useMemo, useState } from "react";

import {
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from "@chakra-ui/react";

import { ChevronDownIcon } from "@chakra-ui/icons";
import { debounce } from "lodash";
import { FiSearch } from "react-icons/fi";
import { useDispatch } from "react-redux";

import { DataType } from "@/jenesys-types/data/enums";

import { useCrudListContext } from "@/components/common/crud/CrudList/CrudListContext";
import { updateQueryParams } from "@/store/crud/list";

interface QueryFiltersProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
}

const typeOptions: Array<{ name: string; value: string }> = [
  { name: "All Types", value: "" },
  { name: "General", value: "GENERAL" },
  { name: "Transaction", value: "TRANSACTION" },
  { name: "Chaser", value: "CHASER" }
];

const statusOptions: Array<{ name: string; value: string }> = [
  { name: "All Statuses", value: "" },
  { name: "New Message", value: "NEW_MESSAGE" },
  { name: "Awaiting Response", value: "AWAITING_RESPONSE" },
  { name: "Resolved", value: "RESOLVED" }
];

export const QueryFilters: React.FC<QueryFiltersProps> = ({
  selectedType,
  onTypeChange,
  selectedStatus,
  onStatusChange
}) => {
  const dispatch = useDispatch();
  const { listId } = useCrudListContext();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleStatusChange = useCallback(
    (status: string) => {
      onStatusChange(status);
    },
    [onStatusChange]
  );

  const handleTypeChange = useCallback(
    (type: string) => {
      onTypeChange(type);

      dispatch(
        updateQueryParams(DataType.CommunicationsQuery, listId, {
          query_type: type || undefined
        })
      );
    },
    [dispatch, listId, onTypeChange]
  );

  const debouncedHandleSearch = useMemo(
    () =>
      debounce((query: string) => {
        dispatch(
          updateQueryParams(DataType.CommunicationsQuery, listId, {
            search: query?.trim() || undefined
          })
        );
      }, 500),
    [dispatch, listId]
  );

  const selectedTypeName = useMemo(
    () =>
      typeOptions.find((option) => option.value === selectedType)?.name ||
      "All Types",
    [selectedType]
  );

  const selectedStatusName = useMemo(
    () =>
      statusOptions.find((option) => option.value === selectedStatus)?.name ||
      "All Statuses",
    [selectedStatus]
  );

  return (
    <HStack w="full" spacing={4}>
      <InputGroup w="full">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.500" />
        </InputLeftElement>
        <Input
          w="full"
          type="search"
          backgroundColor="blackAlpha.50"
          placeholder="Search queries..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedHandleSearch(e.target.value);
          }}
        />
      </InputGroup>

      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          h={10}
          minW="160px"
          p={5}
          textAlign="left"
          size="sm"
          rightIcon={<ChevronDownIcon color="gray.500" fontSize={24} />}
          backgroundColor="blackAlpha.50"
        >
          <Text color="gray.700" fontSize={14}>
            {selectedTypeName}
          </Text>
        </MenuButton>
        <MenuList>
          {typeOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleTypeChange(option.value)}
            >
              <Text fontSize={14}>{option.name}</Text>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Menu>
        <MenuButton
          as={Button}
          variant="outline"
          h={10}
          minW="200px"
          p={5}
          textAlign="left"
          size="sm"
          rightIcon={<ChevronDownIcon color="gray.500" fontSize={24} />}
          backgroundColor="blackAlpha.50"
        >
          <Text color="gray.700" fontSize={14}>
            {selectedStatusName}
          </Text>
        </MenuButton>
        <MenuList>
          {statusOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
            >
              <Text fontSize={14}>{option.name}</Text>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </HStack>
  );
};
